import { useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { supabase } from '@/lib/supabase'
import type { FaqSection } from '@limo/database'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useToast } from '@/hooks/use-toast'
import { Loader2 } from 'lucide-react'

const faqSectionSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .max(100)
    .regex(/^[a-z0-9-]+$/, 'Slug must be lowercase alphanumeric with dashes'),
  description: z.string().max(500).optional().or(z.literal('')),
  display_order: z.coerce.number().int().min(0).default(0),
  is_active: z.boolean().default(true),
})

type FAQSectionFormData = z.infer<typeof faqSectionSchema>

interface FAQSectionDialogProps {
  sectionId: string | null
  accountId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim()
}

export function FAQSectionDialog({
  sectionId,
  accountId,
  open,
  onOpenChange,
}: FAQSectionDialogProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const isEditing = !!sectionId

  const { data: section, isLoading: isLoadingSection } = useQuery({
    queryKey: ['faq-section', sectionId],
    queryFn: async () => {
      if (!sectionId) return null
      const { data, error } = await supabase
        .from('faq_sections')
        .select('*')
        .eq('id', sectionId)
        .single()
      if (error) throw error
      return data as FaqSection
    },
    enabled: !!sectionId && open,
  })

  const form = useForm<FAQSectionFormData>({
    resolver: zodResolver(faqSectionSchema),
    defaultValues: {
      name: '',
      slug: '',
      description: '',
      display_order: 0,
      is_active: true,
    },
  })

  const watchName = form.watch('name')

  useEffect(() => {
    if (!isEditing && watchName && !form.getFieldState('slug').isDirty) {
      form.setValue('slug', generateSlug(watchName))
    }
  }, [watchName, isEditing, form])

  useEffect(() => {
    if (section) {
      form.reset({
        name: section.name,
        slug: section.slug,
        description: section.description || '',
        display_order: section.display_order,
        is_active: section.is_active,
      })
    } else if (!isEditing && open) {
      form.reset({
        name: '',
        slug: '',
        description: '',
        display_order: 0,
        is_active: true,
      })
    }
  }, [section, form, isEditing, open])

  const createMutation = useMutation({
    mutationFn: async (data: FAQSectionFormData) => {
      const { error } = await supabase.from('faq_sections').insert({
        ...data,
        description: data.description || null,
        account_id: accountId,
      })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faq-sections', accountId] })
      queryClient.invalidateQueries({ queryKey: ['faq-sections-list', accountId] })
      toast({
        title: 'Section created',
        description: 'The FAQ section has been successfully created.',
      })
      onOpenChange(false)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const updateMutation = useMutation({
    mutationFn: async (data: FAQSectionFormData) => {
      if (!sectionId) throw new Error('Section ID is required')
      const { error } = await supabase
        .from('faq_sections')
        .update({
          ...data,
          description: data.description || null,
        })
        .eq('id', sectionId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['faq-sections', accountId] })
      queryClient.invalidateQueries({ queryKey: ['faq-sections-list', accountId] })
      queryClient.invalidateQueries({ queryKey: ['faq-section', sectionId] })
      toast({
        title: 'Section updated',
        description: 'The FAQ section has been successfully updated.',
      })
      onOpenChange(false)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const onSubmit = (data: FAQSectionFormData) => {
    if (isEditing) {
      updateMutation.mutate(data)
    } else {
      createMutation.mutate(data)
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      form.reset()
    }
    onOpenChange(open)
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Section' : 'Add Section'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the FAQ section information.'
              : 'Create a new FAQ section to organize your FAQs.'}
          </DialogDescription>
        </DialogHeader>

        {isEditing && isLoadingSection ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Home Page" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="slug"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Slug</FormLabel>
                    <FormControl>
                      <Input placeholder="home-page" {...field} />
                    </FormControl>
                    <FormDescription>
                      URL-friendly identifier (auto-generated from name)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description (Optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="FAQs displayed on the home page..."
                        className="min-h-[80px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="display_order"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Display Order</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} {...field} />
                    </FormControl>
                    <FormDescription>
                      Lower numbers appear first
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="is_active"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Active</FormLabel>
                      <FormDescription>
                        Only active sections are visible to users
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {isEditing ? 'Update Section' : 'Add Section'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  )
}
